

import { Button, Alert, NumberInput, useToast } from "@a2z/ui";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateStoreSubscriptionPaymentMutation } from "@/modules/stores/services/mutations/useCreateStoreSubscriptionPaymentMutation";
import { useCreateSubscriptionPaymentProofUploadMutation } from "@/modules/stores/services/mutations/useCreateSubscriptionPaymentProofUploadMutation";
import { useStoreSubscriptionCurrentScheduleQuery } from "@/modules/stores/services/queries/useStoreSubscriptionCurrentScheduleQuery";

type Props = {
  storeId: string;
  compact?: boolean;
  defaultOpen?: boolean;
  hideToggle?: boolean;
  onSuccess?: () => void;
};

export function SubscriptionPaymentSettleForm({
  storeId,
  compact,
  defaultOpen,
  hideToggle,
  onSuccess,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(
    typeof defaultOpen === "boolean" ? defaultOpen : !compact
  );
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    paidAt?: string;
    amount?: string;
  }>({});
  const { mutateAsync: createPayment, isPending: isCreatingPayment } =
    useCreateStoreSubscriptionPaymentMutation();
  const { mutateAsync: createProofUpload, isPending: isCreatingProofUpload } =
    useCreateSubscriptionPaymentProofUploadMutation();
  const {
    data: scheduleData,
    isLoading: isLoadingSchedule,
    refetch: refetchSchedule,
  } = useStoreSubscriptionCurrentScheduleQuery({
    storeId: isOpen ? storeId : "",
  });
  const schedule = scheduleData ?? null;
  const canMarkAsPaid = (schedule?.remainingAmount ?? 0) > 0;
  const effectiveMarkAsPaid = canMarkAsPaid && markAsPaid;

  const getMutationErrorMessage = (caughtError: unknown) => {
    if (caughtError instanceof ApiJsonClientError) {
      return caughtError.message || t(keys.errors.generic);
    }
    return t(keys.errors.generic);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void refetchSchedule();
  }, [isOpen, refetchSchedule, storeId]);

  const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  };

  const formatAmount = (amount: number | null | undefined, currency?: string) => {
    if (amount === null || amount === undefined) {
      return "-";
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setError(null);
    const nextFieldErrors: { paidAt?: string; amount?: string } = {};
    const formData = new FormData(form);
    const paidAt = formData.get("paidAt");
    const amount = formData.get("amount");
    const paidAtString = typeof paidAt === "string" ? paidAt.trim() : "";
    const amountString = typeof amount === "string" ? amount.trim() : "";

    if (paidAtString) {
      const parsedPaidAt = new Date(`${paidAtString}T00:00:00`);
      if (Number.isNaN(parsedPaidAt.getTime())) {
        nextFieldErrors.paidAt = t(keys.admin.subscriptionPaymentPaidAtInvalid);
      } else {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (parsedPaidAt > today) {
          nextFieldErrors.paidAt = t(keys.admin.subscriptionPaymentPaidAtFuture);
        }
      }
    }

    if (!effectiveMarkAsPaid) {
      if (!amountString) {
        nextFieldErrors.amount = t(keys.admin.subscriptionPaymentAmountRequired);
      } else {
        const parsedAmount = Number(amountString);
        if (!Number.isFinite(parsedAmount)) {
          nextFieldErrors.amount = t(keys.admin.subscriptionPaymentAmountInvalid);
        } else if (parsedAmount <= 0) {
          nextFieldErrors.amount = t(keys.admin.subscriptionPaymentAmountPositive);
        }
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    const proofFile = formData.get("proofFile");

    let proofObjectKey: string | undefined;
    let proofContentType: string | undefined;
    let proofSizeBytes: number | undefined;

    if (proofFile instanceof File && proofFile.size > 0) {
      let uploadPayload: { uploadUrl: string; objectKey: string };
      try {
        uploadPayload = await createProofUpload({
          storeId,
          fileName: proofFile.name,
          contentType: proofFile.type || "application/octet-stream",
          sizeBytes: proofFile.size,
        });
      } catch (caughtError) {
        setError(getMutationErrorMessage(caughtError));
        setIsSubmitting(false);
        return;
      }

      const putResponse = await fetch(uploadPayload.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": proofFile.type || "application/octet-stream" },
        body: proofFile,
      });

      if (!putResponse.ok) {
        setError(t(keys.products.uploadFailed));
        setIsSubmitting(false);
        return;
      }

      proofObjectKey = uploadPayload.objectKey;
      proofContentType = proofFile.type || "application/octet-stream";
      proofSizeBytes = proofFile.size;
    }

    try {
      await createPayment({
        storeId,
        paidAt: paidAtString
          ? new Date(`${paidAtString}T00:00:00`).toISOString()
          : undefined,
        amount: effectiveMarkAsPaid
          ? schedule?.remainingAmount
          : amountString
            ? Number(amountString)
            : undefined,
        markAsPaid: effectiveMarkAsPaid,
        proofObjectKey,
        proofContentType,
        proofSizeBytes,
      });
    } catch (caughtError) {
      setError(getMutationErrorMessage(caughtError));
      setIsSubmitting(false);
      return;
    }

    form.reset();
    setMarkAsPaid(false);
    setFieldErrors({});
    toast.success(t(keys.common.updateSuccess));
    setIsSubmitting(false);
    void refetchSchedule();
    onSuccess?.();
  };

  return (
    <div className="rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3">
      {compact && !hideToggle ? (
        <Button
          type="button"
          variant="outline" className="h-9 rounded-full px-3 text-xs font-semibold transition"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {isOpen ? t(keys.common.hide) : t(keys.admin.storeSubscriptionsSettlePayment)}
        </Button>
      ) : null}
      {isOpen ? (
        <form className="mt-3 grid gap-3 text-sm" onSubmit={handleSubmit}>
          {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
          <div className="rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 py-2 text-xs">
            <div className="text-[13px] font-semibold text-[color:var(--text-secondary)]">
              {t(keys.admin.subscriptionScheduleCurrentTitle)}
            </div>
            {isLoadingSchedule ? (
              <div className="mt-2 flex items-center gap-2 text-[13px] text-[color:var(--text-muted)]">
                <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-[color:var(--border-light)] border-t-transparent" />
                {t(keys.common.loading)}
              </div>
            ) : schedule ? (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[color:var(--text-muted)]">
                    {t(keys.admin.subscriptionScheduleDueDate)}
                  </span>
                  <span className="text-[color:var(--text-primary)]">
                    {formatDate(schedule.dueDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[color:var(--text-muted)]">
                    {t(keys.admin.subscriptionScheduleStatus)}
                  </span>
                  <span className="text-[color:var(--text-primary)]">
                    {schedule.status}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[color:var(--text-muted)]">
                    {t(keys.admin.subscriptionSchedulePaid)}
                  </span>
                  <span className="text-[color:var(--text-primary)]">
                    {formatAmount(schedule.paidAmount, schedule.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[color:var(--text-muted)]">
                    {t(keys.admin.subscriptionScheduleRemaining)}
                  </span>
                  <span className="text-[color:var(--text-primary)]">
                    {formatAmount(schedule.remainingAmount, schedule.currency)}
                  </span>
                </div>
                {schedule.creditBalance > 0 ? (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[color:var(--text-muted)]">
                      {t(keys.admin.storeSubscriptionsCreditBalance)}
                    </span>
                    <span className="text-[color:var(--text-primary)]">
                      {formatAmount(schedule.creditBalance, schedule.currency)}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-2 text-[13px] text-[color:var(--text-muted)]">
                {t(keys.common.emptyTitle)}
              </div>
            )}
          </div>
          <label className="flex flex-col gap-2 text-xs font-medium text-[color:var(--text-secondary)]">
            <span>{t(keys.admin.subscriptionPaymentPaidAtLabel)}</span>
            <input
              name="paidAt"
              type="date"
              aria-invalid={fieldErrors.paidAt ? "true" : undefined}
              onChange={() =>
                setFieldErrors((prev) => {
                  if (!prev.paidAt) {
                    return prev;
                  }
                  return { ...prev, paidAt: undefined };
                })
              }
              className="h-10 w-full rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-sm focus:outline-none focus:border-[color:var(--primary-500)]"
            />
            {fieldErrors.paidAt ? (
              <span className="text-[13px] text-[color:var(--error-500)]">
                {fieldErrors.paidAt}
              </span>
            ) : null}
          </label>
          <label className="flex flex-col gap-2 text-xs font-medium text-[color:var(--text-secondary)]">
            <span>{t(keys.admin.subscriptionPaymentAmountLabel)}</span>
            <NumberInput
              name="amount"
              min={0}
              step={0.01}
              allowDecimal
              disabled={effectiveMarkAsPaid}
              hasError={Boolean(fieldErrors.amount)}
              onChange={() =>
                setFieldErrors((prev) => {
                  if (!prev.amount) {
                    return prev;
                  }
                  return { ...prev, amount: undefined };
                })
              }
            />
            {fieldErrors.amount ? (
              <span className="text-[13px] text-[color:var(--error-500)]">
                {fieldErrors.amount}
              </span>
            ) : null}
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-[color:var(--text-secondary)]">
            <input
              type="checkbox"
              checked={effectiveMarkAsPaid}
              onChange={(event) => {
                const checked = canMarkAsPaid && event.target.checked;
                setMarkAsPaid(checked);
                if (checked) {
                  setFieldErrors((prev) => ({ ...prev, amount: undefined }));
                }
              }}
              className="h-4 w-4"
              disabled={!canMarkAsPaid}
            />
            {t(keys.admin.subscriptionPaymentMarkAsPaid)}
          </label>
          <label className="flex flex-col gap-2 text-xs font-medium text-[color:var(--text-secondary)]">
            <span>{t(keys.admin.subscriptionPaymentProofLabel)}</span>
            <input
              name="proofFile"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="text-xs text-[color:var(--text-muted)]"
            />
            <span className="text-[13px] text-[color:var(--text-muted)]">
              {t(keys.admin.subscriptionPaymentProofHelper)}
            </span>
          </label>
          <Button
            type="submit"
            disabled={isSubmitting || isCreatingPayment || isCreatingProofUpload}
            variant="primary" className="h-10 rounded-lg px-4 text-xs font-semibold transition disabled:opacity-60"
          >
            {isSubmitting ? t(keys.common.loading) : t(keys.common.save)}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
